import { dispatch } from "./mod-cart-event-bus.js";

interface CouponValidationSettings {
  enabled: boolean;
  exceptions: string[];
}

class MinicartCouponValidation {
  private readonly settings: CouponValidationSettings;
  private isUpdating = false;

  constructor() {
    const validationConfig = window.insiderTheme?.couponValidation;
    this.settings = {
      enabled: validationConfig?.enabled || false,
      exceptions: validationConfig?.exceptions || [],
    };
  }

  public init(): void {
    if (!this.settings.enabled) {
      return;
    }

    this.validateCurrentCartState();
  }

  private async validateCurrentCartState(): Promise<void> {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      if (cart) {
        this.handleCartUpdate({ cart });
      }
    } catch (error) {
      console.error("Error fetching initial cart state for validation:", error);
    }
  }

  private intToBRL(value: number): string {
    return (value / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  private async handleCartUpdate({ cart }: { cart: CartAPI }): Promise<void> {
    if (this.isUpdating || !cart.discount_codes || cart.discount_codes.length <= 1) {
      return;
    }

    const exactExceptions = new Set(this.settings.exceptions.filter((e) => !e.includes("*")));
    const prefixExceptions = this.settings.exceptions.filter((e) => e.includes("*")).map((e) => e.replace("*", ""));

    const appliedCodes = cart.discount_codes.filter((d) => d.applicable).map((d) => d.code);

    const appliedCodesWithoutExceptions = appliedCodes.filter((code) => {
      if (exactExceptions.has(code)) return false;
      return !prefixExceptions.some((prefix) => code.startsWith(prefix));
    });

    if (appliedCodesWithoutExceptions.length <= 1) {
      return;
    }

    const couponToRemove = appliedCodesWithoutExceptions[appliedCodesWithoutExceptions.length - 1];
    const finalCodes = appliedCodes.filter((code) => code !== couponToRemove);

    this.isUpdating = true;

    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discount: finalCodes.join(","),
        }),
      });

      // H4: valida HTTP status antes de parsear. Shopify retorna 422 com corpo JSON
      // de erro (não um cart) em falhas de validação de cupom — sem isso, dispatch()
      // receberia um objeto malformado e cartFingerprint() quebraria em cart.items.map.
      if (!response.ok) {
        throw new Error(`Cart update failed with status ${response.status}`);
      }

      const updateResponse = await response.json();

      // H1: textContent (não innerHTML). intToBRL retorna string formatada sem HTML,
      // e o guard de tipo numérico protege contra valores inesperados que poderiam
      // ser interpretados como markup se um dia a função for alterada.
      if (typeof updateResponse.total_price === "number" && Number.isFinite(updateResponse.total_price)) {
        const subtotalEl = document.getElementsByClassName("subtotal__header--value")[0] as HTMLElement | undefined;
        if (subtotalEl) {
          subtotalEl.textContent = this.intToBRL(updateResponse.total_price);
        }
      }

      // Só dispatcha se o payload tem shape mínimo de cart (token + items array).
      // Defense-in-depth além do isValidCart() no dispatch() do bus.
      if (
        updateResponse &&
        typeof updateResponse === "object" &&
        typeof updateResponse.token === "string" &&
        Array.isArray(updateResponse.items)
      ) {
        dispatch({ source: "minicart-coupon", operation: "update-discount", cart: updateResponse });
      }
    } catch (error) {
      console.error("Error updating cart discounts:", error);
    } finally {
      setTimeout(() => {
        this.isUpdating = false;
      }, 500);
    }
  }
}

new MinicartCouponValidation().init();
